import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { StdCfgSharedServiceService } from "@app/cem/project/standard-config/std-cfg-shared-service.service";
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { IFunctionDto } from '@app/shared/models/dtos/function';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { SplitterModule } from '@progress/kendo-angular-layout';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StgCfgDetailsComponent } from '../components/stg-cfg-details/stg-cfg-details.component';
import { StgCfgItemsComponent } from '../components/stg-cfg-items/stg-cfg-items.component';
import { StgCfgPadComponent } from '../components/stg-cfg-pad/stg-cfg-pad.component';
import { IStandardConfigDto } from './../../../../shared/models/dtos/standard-config';

@Component({
  selector: 'app-std-cfg-index',
  templateUrl: './std-cfg-index.component.html',
  styleUrls: ['./std-cfg-index.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    DialogActionsComponent,
    DialogComponent,
    DropDownListComponent,
    SplitterModule,
    StgCfgDetailsComponent,
    StgCfgItemsComponent,
    StgCfgPadComponent,
  ],
})
export class StdCfgIndexComponent implements OnInit, OnDestroy {
  public stdCfgSharedServiceService = inject(StdCfgSharedServiceService);

  public icons = kendoIcons;
  public dialogOpened = false;
  selectedFunction: IFunctionDto;
  selectedStdConfig: IStandardConfigDto;
  pendingFunction: IFunctionDto;
  unsubscribeStdConfig$: Subject<boolean> = new Subject();
  public readonly FunctionDropdownList = viewChild<DropDownListComponent>('functionDropdownList');
  public readonly detailsComponent = viewChild(StgCfgDetailsComponent);
  public readonly itemsComponent = viewChild(StgCfgItemsComponent);

  async ngOnInit() {
    await this.stdCfgSharedServiceService.loadLookups();
    this.stdCfgSharedServiceService.getSelectedStandardConfigDto().pipe(takeUntil(this.unsubscribeStdConfig$)).subscribe(response => {
      if (response != null) {
        this.selectedStdConfig = response;
      }
    }
    );
  }

  ngOnDestroy() {
    this.unsubscribeStdConfig$.next(true);
    this.unsubscribeStdConfig$.complete();
  }

  public async onValueChange(value) {
    // no changes or edits, so switch to selected standard config
    if (this.selectedFunction === null || this.selectedFunction === undefined || !this.stdCfgSharedServiceService.editMode) {
      this.selectedFunction = value;
      await this.stdCfgSharedServiceService.setSelectedFunctionDto(this.selectedFunction);
    }
    // changes to the standard config details, but there hasnt already been a dialog box popup occured yet (this is a fix for a weird multi onSelectionChanged call.) Call for the dialog box.
    else {
      this.pendingFunction = value;
      this.dialogOpened = true;
    }
    this.itemsComponent().toggleScopingCategoryColumn(this.selectedFunction);
  }

  public confirmCancel() {
    this.FunctionDropdownList().value = this.selectedFunction;
    this.pendingFunction = null;
    this.dialogOpened = false;
  }

  public async confirmSave() {
    let saved = await this.stdCfgSharedServiceService.saveChanges();
    if (saved) {
      this.selectedFunction = this.pendingFunction;
      this.pendingFunction = null;
      await this.stdCfgSharedServiceService.setSelectedFunctionDto(this.selectedFunction);
    } else {
      this.FunctionDropdownList().value = this.selectedFunction;
      this.pendingFunction = null;
    }
    this.dialogOpened = false;
    this.itemsComponent().toggleScopingCategoryColumn(this.selectedFunction);
  }

  public async confirmDontSave() {
    this.selectedFunction = this.pendingFunction;
    this.pendingFunction = null;
    this.dialogOpened = false;
    await this.stdCfgSharedServiceService.setSelectedFunctionDto(this.selectedFunction);
    await this.stdCfgSharedServiceService.cancelNewStandardConfigDto();
    this.itemsComponent().toggleScopingCategoryColumn(this.selectedFunction);
  }

  addNewItem() {
    this.detailsComponent().addNewStandardConfigDto(this.selectedFunction);
  }

  copyStdConfig() {
    this.stdCfgSharedServiceService.copyStdConfig(this.selectedStdConfig).then((response) => {
      this.stdCfgSharedServiceService.setSelectedFunctionDto(this.selectedFunction);
    });
  }
}